import {
    useState
} from "react";
import {
    ComponentHeader,
    AnatomySection,
    ExampleSection,
    getSectionToc,
    PropsTable
} from "./shared";
import {
    useDocsI18n
} from "../../i18n";
import type {
    DocsTranslate
} from "../../anatomy/types";
import {
    ColorSwatch
} from "ncore-ui-kit";
import {
    BulletList,
    Paragraph,
    CodeBlock,
    Article,
    Preview,
    Section
} from "../../components/content";

const NAME = "ColorSwatch";

const USAGE_CODE = `<ColorSwatch
    title="Teal"
    color="#2f8f83"
/>`;

const PRESS_CODE = `const [isSelected, setIsSelected] = useState(false);

<ColorSwatch
    onPress={() => setIsSelected(!isSelected)}
    isSelected={isSelected}
    title="Teal"
    color="#2f8f83"
/>`;

const SIZES = [
    "small",
    "medium",
    "large"
] as const;

const COLORS = [
    "#2f8f83",
    "#b4532a",
    "#3b6fb6",
    "#c79a1e"
] as const;

const BASE_COLOR = "#2f8f83";

const TRANSPARENT_COLORS = [
    "#2f8f83ff",
    "#2f8f83b3",
    "#2f8f8366",
    "#2f8f8333"
] as const;

const LIGHT_COLORS = [
    "#ffffff",
    "#fafafa",
    "#f2f2f2",
    "#d0d0d0"
] as const;

export const getToc = (t: DocsTranslate) => getSectionToc([
    {
        title: t("section.overview"),
        id: "overview"
    },
    {
        title: t("section.sizes"),
        id: "sizes"
    },
    {
        title: t("colorSwatchPage.transparencyTitle"),
        id: "transparency"
    },
    {
        title: t("colorSwatchPage.edgeTitle"),
        id: "edge"
    },
    {
        title: t("section.states"),
        id: "states"
    },
    {
        title: t("section.accessibility"),
        id: "accessibility"
    },
    {
        title: t("section.props"),
        id: "props"
    }
], NAME, t);

const ColorSwatchPage = () => {
    const {
        t
    } = useDocsI18n();

    const [
        isSelected,
        setIsSelected
    ] = useState<boolean>(true);

    return <Article>
        <ComponentHeader
            lead={t("colorSwatchPage.lead")}
            name={NAME}
        />
        <Section
            title={t("section.overview")}
            id="overview"
        >
            <Paragraph>
                {t("colorSwatchPage.overviewText")}
            </Paragraph>
            <Preview
                caption={t("colorSwatchPage.overviewCaption")}
            >
                {
                    COLORS.map((color) => <ColorSwatch
                        color={color}
                        key={color}
                    />)
                }
            </Preview>
            <CodeBlock
                code={USAGE_CODE}
                language="tsx"
            />
        </Section>
        <AnatomySection
            name={NAME}
        />
        <Section
            title={t("section.sizes")}
            id="sizes"
        >
            <Paragraph>
                {t("colorSwatchPage.sizesText")}
            </Paragraph>
            <Preview>
                {
                    SIZES.map((size) => <ColorSwatch
                        color={BASE_COLOR}
                        size={size}
                        key={size}
                    />)
                }
            </Preview>
        </Section>
        <Section
            title={t("colorSwatchPage.transparencyTitle")}
            id="transparency"
        >
            <Paragraph>
                {t("colorSwatchPage.transparencyText")}
            </Paragraph>
            <Preview>
                {
                    TRANSPARENT_COLORS.map((color) => <ColorSwatch
                        color={color}
                        size="large"
                        key={color}
                    />)
                }
            </Preview>
        </Section>
        <Section
            title={t("colorSwatchPage.edgeTitle")}
            id="edge"
        >
            <Paragraph>
                {t("colorSwatchPage.edgeText")}
            </Paragraph>
            <Preview>
                {
                    LIGHT_COLORS.map((color) => <ColorSwatch
                        color={color}
                        size="large"
                        key={color}
                    />)
                }
            </Preview>
        </Section>
        <Section
            title={t("section.states")}
            id="states"
        >
            <Paragraph>
                {t("colorSwatchPage.statesText")}
            </Paragraph>
            <Preview
                caption={t("colorSwatchPage.statesCaption")}
            >
                <ColorSwatch
                    onPress={() => setIsSelected(!isSelected)}
                    title={t("colorSwatchPage.stateToggle")}
                    isSelected={isSelected}
                    color={BASE_COLOR}
                    size="large"
                />
                <ColorSwatch
                    title={t("colorSwatchPage.stateDisabled")}
                    color={BASE_COLOR}
                    isDisabled={true}
                    size="large"
                />
            </Preview>
            <CodeBlock
                code={PRESS_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("section.accessibility")}
            id="accessibility"
        >
            <BulletList
                items={[
                    t("colorSwatchPage.a11yRole"),
                    t("colorSwatchPage.a11yName"),
                    t("colorSwatchPage.a11yDecorative"),
                    t("colorSwatchPage.a11yKeyboard"),
                    t("colorSwatchPage.a11yColour"),
                    t("colorSwatchPage.a11yTooltip")
                ]}
            />
        </Section>
        <Section
            title={t("section.props")}
            id="props"
        >
            <PropsTable
                rows={[
                    {
                        description: t("colorSwatchPage.propColor"),
                        type: "string",
                        name: "color"
                    },
                    {
                        description: t("colorSwatchPage.propTitle"),
                        type: "string",
                        name: "title"
                    },
                    {
                        description: t("colorSwatchPage.propSize"),
                        type: "\"small\" | \"medium\" | \"large\"",
                        defaultValue: "\"medium\"",
                        name: "size"
                    },
                    {
                        description: t("colorSwatchPage.propOnPress"),
                        type: "() => void",
                        name: "onPress"
                    },
                    {
                        description: t("colorSwatchPage.propIsSelected"),
                        name: "isSelected",
                        type: "boolean"
                    },
                    {
                        description: t("colorSwatchPage.propRole"),
                        type: "\"button\" | \"radio\"",
                        defaultValue: "\"button\"",
                        name: "role"
                    },
                    {
                        description: t("colorSwatchPage.propSurfaceColor"),
                        name: "surfaceColor",
                        type: "string"
                    },
                    {
                        description: t("colorSwatchPage.propIsTooltipEnabled"),
                        defaultValue: "!!title && !!onPress",
                        name: "isTooltipEnabled",
                        type: "boolean"
                    },
                    {
                        description: t("colorSwatchPage.propIsDisabled"),
                        defaultValue: "false",
                        name: "isDisabled",
                        type: "boolean"
                    },
                    {
                        type: "{ themeKey, paletteKey, sharpness, gapPropagation }",
                        description: t("colorSwatchPage.propCustomTheme"),
                        name: "customTheme"
                    },
                    {
                        description: t("colorSwatchPage.propCustomLocalize"),
                        type: "{ activeLocale }",
                        name: "customLocalize"
                    }
                ]}
            />
        </Section>
        <ExampleSection
            name={NAME}
        />
    </Article>;
};
export default ColorSwatchPage;
